import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2, Edit2, Plus } from "lucide-react";

export default function AdminSubscriptions() {
  const { t } = useI18n();
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newSub, setNewSub] = useState({ user_id: "", plan_id: "", frequency: "monthly", status: "active" });

  const fetchSubs = () => {
    Promise.all([
      api.get("/admin/subscriptions"),
      api.get("/admin/pricing-matrix"),
      api.get("/admin/customers"),
    ]).then(([s, p, c]) => {
      setSubs(s.data);
      setPlans(p.data);
      setCustomers(c.data);
      setSelected(new Set());
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetchSubs(); }, []);

  const deleteSub = async (id) => {
    if (!window.confirm(t("admin.delete_confirm"))) return;
    await api.delete(`/admin/subscriptions/${id}`);
    toast.success("Abonnement verwijderd");
    fetchSubs();
  };

  const bulkDelete = async () => {
    if (!window.confirm(`${selected.size} abonnementen verwijderen?`)) return;
    await api.post("/admin/subscriptions/bulk-delete", { ids: [...selected] });
    toast.success(`${selected.size} abonnementen verwijderd`);
    fetchSubs();
  };

  const bulkDeleteByStatus = async (status) => {
    const count = subs.filter(s => s.status === status).length;
    if (!window.confirm(`Alle ${count} "${status}" abonnementen verwijderen?`)) return;
    const res = await api.post("/admin/subscriptions/bulk-delete", { status });
    toast.success(`${res.data.deleted} abonnementen verwijderd`);
    fetchSubs();
  };

  const handleUpdateSub = async () => {
    try {
      await api.put(`/admin/subscriptions/${editing.id}`, {
        status: editing.status,
        plan_name: editing.plan_name,
        frequency: editing.frequency,
      });
      toast.success("Abonnement bijgewerkt");
      setEditing(null);
      fetchSubs();
    } catch { toast.error("Fout bij opslaan"); }
  };

  const handleCreateSub = async () => {
    try {
      await api.post("/admin/subscriptions", newSub);
      toast.success("Abonnement aangemaakt");
      setShowCreate(false);
      setNewSub({ user_id: "", plan_id: "", frequency: "monthly", status: "active" });
      fetchSubs();
    } catch (err) { toast.error(err.response?.data?.detail || "Fout bij aanmaken"); }
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(selected.size === subs.length ? new Set() : new Set(subs.map(s => s.id)));

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  const deletedCount = subs.filter(s => s.status === "deleted").length;
  const cancelledCount = subs.filter(s => s.status === "cancelled").length;
  const usersWithoutSub = customers.filter(c => !subs.find(s => s.user_id === c.id));

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("admin.subscriptions")}</h1>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{subs.length} totaal</Badge>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="add-sub-btn">
                <Plus className="w-4 h-4 mr-2" />Abonnement Aanmaken
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nieuw Abonnement Aanmaken</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label>Klant *</Label>
                  <Select value={newSub.user_id} onValueChange={(v) => setNewSub({...newSub, user_id: v})}>
                    <SelectTrigger className="mt-1" data-testid="new-sub-user"><SelectValue placeholder="Selecteer klant..." /></SelectTrigger>
                    <SelectContent>
                      {usersWithoutSub.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                      ))}
                      {usersWithoutSub.length === 0 && <SelectItem value="" disabled>Alle klanten hebben al een abonnement</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Plan *</Label>
                  <Select value={newSub.plan_id} onValueChange={(v) => setNewSub({...newSub, plan_id: v})}>
                    <SelectTrigger className="mt-1" data-testid="new-sub-plan"><SelectValue placeholder="Selecteer plan..." /></SelectTrigger>
                    <SelectContent>
                      {plans.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name} ({p.product_limit} producten)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Frequentie</Label>
                    <Select value={newSub.frequency} onValueChange={(v) => setNewSub({...newSub, frequency: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Maandelijks</SelectItem>
                        <SelectItem value="weekly">Wekelijks</SelectItem>
                        <SelectItem value="daily">Dagelijks</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={newSub.status} onValueChange={(v) => setNewSub({...newSub, status: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Actief</SelectItem>
                        <SelectItem value="trial">Trial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Annuleren</Button>
                <Button onClick={handleCreateSub} className="bg-blue-600" disabled={!newSub.user_id || !newSub.plan_id} data-testid="create-sub-btn">Aanmaken</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {selected.size > 0 && (
          <Button variant="destructive" size="sm" onClick={bulkDelete}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />{selected.size} {t("admin.bulk_delete")}
          </Button>
        )}
        {deletedCount > 0 && (
          <Button variant="outline" size="sm" className="text-destructive border-destructive/30" onClick={() => bulkDeleteByStatus("deleted")}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />Alle "deleted" verwijderen ({deletedCount})
          </Button>
        )}
        {cancelledCount > 0 && (
          <Button variant="outline" size="sm" className="text-amber-500 border-amber-500/30" onClick={() => bulkDeleteByStatus("cancelled")}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />Alle "cancelled" verwijderen ({cancelledCount})
          </Button>
        )}
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"><Checkbox checked={subs.length > 0 && selected.size === subs.length} onCheckedChange={toggleAll} /></TableHead>
              <TableHead>Gebruiker</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Frequentie</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Aangemaakt</TableHead>
              <TableHead className="text-right">Acties</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subs.map((s) => (
              <TableRow key={s.id} className={`table-row-hover ${selected.has(s.id) ? "bg-primary/5" : ""}`}>
                <TableCell><Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggleSelect(s.id)} /></TableCell>
                <TableCell className="font-medium">{s.user?.name || "-"}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{s.user?.email || "-"}</TableCell>
                <TableCell><Badge variant="outline">{s.plan_name}</Badge></TableCell>
                <TableCell className="capitalize">{s.frequency}</TableCell>
                <TableCell>
                  <Badge variant={s.status === "active" ? "default" : s.status === "trial" ? "secondary" : "destructive"}
                         className={s.status === "active" ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}>
                    {s.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{new Date(s.created_at).toLocaleDateString("nl-NL")}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing({...s})} title="Bewerken">
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteSub(s.id)} title={t("admin.delete")}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {editing && (
        <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Abonnement Bewerken</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-accent/30 rounded-lg p-3 text-sm">
                <p><strong>Klant:</strong> {editing.user?.name} ({editing.user?.email})</p>
              </div>
              <div>
                <Label>Plan Naam</Label>
                <Input value={editing.plan_name || ""} onChange={(e) => setEditing({...editing, plan_name: e.target.value})} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Frequentie</Label>
                  <Select value={editing.frequency} onValueChange={(v) => setEditing({...editing, frequency: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Maandelijks</SelectItem>
                      <SelectItem value="weekly">Wekelijks</SelectItem>
                      <SelectItem value="daily">Dagelijks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({...editing, status: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Actief</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="cancelled">Geannuleerd</SelectItem>
                      <SelectItem value="expired">Verlopen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Annuleren</Button>
              <Button onClick={handleUpdateSub} className="bg-blue-600" data-testid="save-sub-btn">Opslaan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
